import emptyTrips from "../../assets/illustrations/empty-trips.png";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Group, Select, Text, SimpleGrid } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { TripStatus } from "@safiri/shared";
import { tripsApi } from "../../api/trips";
import type { Trip } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { SearchInput } from "../../components/common/SearchInput";
import { DataTable, type Column } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { StatCard } from "../../components/common/StatCard";
import { formatMoney, formatNumber, formatDateTime, tripRef, tripFare } from "../../lib/format";
import { humanize } from "../../lib/labels";
import { DriverLink, UserLink } from "../../components/common/EntityLink";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import { errorMessage } from "../../lib/axios";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  ...Object.values(TripStatus).map((s) => ({ value: s, label: humanize(s) })),
];

// Part wallet / part cash filter; the inbox links to ?partPayment=unconfirmed.
const PART_PAYMENT_OPTIONS = [
  { value: "", label: "All payments" },
  { value: "any", label: "Wallet + cash" },
  { value: "unconfirmed", label: "Wallet + cash, cash unconfirmed" },
];

const paymentLabel = (t: Trip) =>
  t.paymentDetail?.split
    ? "Wallet + cash"
    : t.paymentDetail?.paymentMode
      ? humanize(t.paymentDetail.paymentMode)
      : "—";

const tripExportFields: ExportField<Trip>[] = [
  { label: "Trip", value: (t) => tripRef(t) },
  { label: "Rider", value: (t) => t.passengerDetail?.name },
  { label: "Driver", value: (t) => t.driverDetail?.name },
  { label: "Fare", value: (t) => tripFare(t) },
  { label: "Payment", value: (t) => paymentLabel(t) },
  { label: "Status", value: (t) => t.tripStatus },
  { label: "Created", value: (t) => t.createdAt },
];

export function TripsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [partPayment, setPartPayment] = useState(() => {
    const v = searchParams.get("partPayment");
    return v === "any" || v === "unconfirmed" ? v : "";
  });
  const [range, setRange] = useState<DateRange>({});
  const filters = {
    status,
    startDate: range.startDate,
    endDate: range.endDate,
    partPayment: (partPayment || undefined) as "any" | "unconfirmed" | undefined,
  };

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<Trip>({
      queryKey: ["trips"],
      fetcher: tripsApi.list,
      filters,
    });

  // Trip stats card row (admin-only endpoint).
  const { data: stats } = useQuery({
    queryKey: ["trip-stats"],
    queryFn: () => tripsApi.stats(),
  });

  const columns: Column<Trip>[] = [
    {
      key: "id",
      header: "Trip",
      render: (t) => (
        <Text size="sm" ff="monospace">
          {tripRef(t)}
        </Text>
      ),
    },
    {
      key: "rider",
      header: "Rider",
      render: (t) => (
        <UserLink user={t.passengerDetail?.passengerID} name={t.passengerDetail?.name} />
      ),
    },
    {
      key: "driver",
      header: "Driver",
      render: (t) => (
        <DriverLink driver={t.driverDetail?.driverID} name={t.driverDetail?.name} />
      ),
    },
    {
      key: "fare",
      header: "Fare",
      align: "right",
      render: (t) => formatMoney(tripFare(t)),
    },
    {
      key: "payment",
      header: "Payment",
      render: (t) => paymentLabel(t),
    },
    {
      key: "status",
      header: "Status",
      render: (t) => <StatusBadge status={t.tripStatus} />,
    },
    {
      key: "created",
      header: "Created",
      render: (t) => (
        <Text size="xs" c="dimmed">
          {formatDateTime(t.createdAt)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Trips" subtitle="Ride history & live status" />

      {stats && (
        <SimpleGrid cols={{ base: 2, md: 4 }} mb="lg">
          <StatCard label="Total trips" value={formatNumber(stats.totalTrips)} />
          <StatCard label="Completed" value={formatNumber(stats.completedTrips)} />
          <StatCard label="Cancelled" value={formatNumber(stats.cancelledTrips)} />
          <StatCard label="Revenue" value={formatMoney(stats.totalRevenue)} />
        </SimpleGrid>
      )}

      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search rider, driver, trip id…"
          />
          <Select
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            w={200}
            allowDeselect={false}
          />
          <Select
            data={PART_PAYMENT_OPTIONS}
            value={partPayment}
            onChange={(v) => {
              setPartPayment(v ?? "");
              setPage(1);
            }}
            w={240}
            allowDeselect={false}
            aria-label="Payment filter"
          />
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          <ExportMenu
            filename="trips"
            fields={tripExportFields}
            fetchAll={() =>
              collectAll(tripsApi.list, {
                search: search || undefined,
                ...filters,
              })
            }
          />
        </Group>

        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(t) => t._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No trips found"
          emptyImage={emptyTrips}
          onRowClick={(t) => navigate(`/trips/${t._id}`)}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>
    </>
  );
}
